import { useUser } from '../../context/userContext';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiArrowUpRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { PulseLoader } from 'react-spinners';
import CountUp from 'react-countup';
import { useState, useEffect } from 'react';

export default function BalanceCard() {
  const { 
    adsBalance, 
    loading,
    user,
    withdrawalHistory = []
  } = useUser();
  
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);

  // Set progress based on withdrawal history
  useEffect(() => {
    if (withdrawalHistory.length > 0) {
      // Calculate progress (example: 65% of monthly limit used)
      const limitUsed = Math.min(65, 100); // Replace with actual logic
      setProgress(limitUsed);
    }
  }, [withdrawalHistory]);

  const quickWithdrawAmounts = [1,5,10,50];

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-700 flex items-center">
          <FiDollarSign className="mr-2" />
          Ads Balance
        </h2>
        {user?.premium && (
          <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
            PRO
          </span>
        )}
      </div>

      {/* Balance Display */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-24">
          <PulseLoader color="#6366f1" size={10} />
          <span className="text-gray-400 text-sm mt-2">Loading balance...</span>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <p className="text-3xl font-bold text-gray-900 text-center">
            <CountUp
              end={adsBalance || 0}
              decimals={adsBalance < 1 ? 3 : 2}
              prefix="$shib"
              duration={0.8}
              separator=","
            />
          </p>
          <p className="text-center text-sm text-gray-500 mt-1">
            Available for withdrawal
          </p>
        </motion.div>
      )}

      {/* Withdrawal Progress */}
      {progress > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Withdrawal limit</span>
            <span>{progress}% used</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {showTooltip && (
                <div className="relative -mt-6 -ml-4">
                  <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded">
                    $shib{(progress * 100).toLocaleString()} of $10,000 limit
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Withdraw Buttons */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {quickWithdrawAmounts.map(amount => (
          <motion.button
            key={amount}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-indigo-600 py-2 px-1 rounded-lg font-medium text-sm border border-indigo-100 hover:bg-indigo-50 transition-colors"
            onClick={() => navigate(`/withdrawx?amount=${amount}`)}
            disabled={loading || (adsBalance < amount)}
          >
            {amount}k
          </motion.button>
        ))}
      </div>

      {/* Main Withdraw Button */}
      <div className="grid grid-cols-1 gap-3">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/withdrawx')}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center shadow-md hover:shadow-lg transition-all"
          disabled={loading}
        >
          Withdraw Funds
          <FiArrowUpRight className="ml-2" />
        </motion.button>
      </div>
    </div>
  );
}
